import { DOMParser, XMLHttpRequest } from 'global';

const defaultOptions = {
  data: null,
  url: '',
  async: true,
  timeout: 5000,
  method: 'GET',
  username: undefined,
  password: undefined,
  headers: {},
  withCredentials: false
};

// Polyfill DOMException
let DOMException = window.DOMException;

try {
  throw new DOMException();
} catch (err) {
  DOMException = function DOMException (message, name) {
    this.message = message;
    this.name = name;
    const error = new Error(message);
    this.stack = error.stack;
  };
  DOMException.prototype = Object.create(Error.prototype);
  DOMException.prototype.constructor = DOMException;
}

/**
 * Normalize a header name
 *
 * @param {String} name
 */
function normalizeName (name) {
  return String(name).toLowerCase();
}

/**
 * Normalize a header value
 *
 * @param {String} value
 */
function normalizeValue (value) {
  return String(value);
}

/**
 * Parse headers from a string
 *
 * @param {String} strHeaders
 */
function parseHeaders (strHeaders) {
  const headers = {
    map: {}
  };

  /**
   * Append a header
   *
   * @param {String} name  Header name
   * @param {String} value Header value
   */
  headers.append = function append (name, value) {
    name = normalizeName(name);
    value = normalizeValue(value);
    if (this.map[name]) {
      this.map[name] += `, ${value}`;
    } else {
      this.map[name] = value;
    }
  };

  /**
   * Retrieve a header value
   *
   * @param {String} name
   *
   * @return {String}
   */
  headers.get = function get (name) {
    return this.map[normalizeName(name)];
  };

  /**
   * Set a header value
   *
   * @param {String} name
   * @param {String} value
   */
  headers.set = function set (name, value) {
    this.map[normalizeName(name)] = normalizeValue(value);
  };

  /**
   * Check if a header exists
   *
   * @param {String} name
   *
   * @return {Boolean}
   */
  headers.has = function has (name) {
    return this.map.hasOwnProperty(normalizeName(name));
  };

  // parse and build header mappings
  strHeaders.replace(/\r?\n[\t ]+/g, ' ')
    .split(/\r?\n/)
    .forEach(function (line) {
      const parts = line.split(':');
      const key = parts.shift().trim();
      if (key) {
        headers.append(key, parts.join(':').trim());
      }
    });

  return headers;
}

/**
 * Wraps XMLHttpRequest inside of a Promise that mimics fetch
 *
 * @param {String} url      The url to be requested
 * @param {Object} options  Additional request options
 *
 * @return {Promise}
 */
export default function ajax (url, options = {}) {
  options = Object.assign({}, defaultOptions, options);

  const xhr = new XMLHttpRequest();
  const promise = new Promise(function (resolve, reject) {
    xhr.onload = function ajaxOnLoad () {
      resolve({
        status: xhr.status || 200,
        statusText: xhr.statusText || 'OK',
        headers: parseHeaders(xhr.getAllResponseHeaders()),
        url: xhr.responseURL || xhr.getResponseHeader('X-Request-URL') || '',
        ok: xhr.status >= 200 && xhr.status < 300,
        xml: function ajaxResponseXML () {
          if (xhr.responseXML) {
            return Promise.resolve(xhr.responseXML);
          } else {
            var parser = new DOMParser();
            return Promise.resolve(parser.parseFromString(xhr.responseText, 'application/xml'));
          }
        },
        text: function ajaxResponseText () {
          return Promise.resolve(xhr.responseText);
        },
        json: function ajaxResponseJson () {
          return Promise.resolve(this.text().then(JSON.parse));
        }
      });
    };

    xhr.onerror = function ajaxOnError () {
      const err = new TypeError('Network request failed');
      err.name = 'Network';
      reject(err);
    };

    xhr.ontimeout = function ajaxOnTimeout () {
      const err = new TypeError('Network request timed out');
      err.name = 'Timeout';
      reject(err);
    };

    xhr.onabort = function ajaxOnAbort () {
      reject(new DOMException('Aborted', 'AbortError'));
    };

    try {
      xhr.open(options.method, url || options.url, options.async, options.username, options.password);

      xhr.timeout = options.timeout;

      Object.keys(options.headers).forEach(function ajaxApplyHeader (name) {
        xhr.setRequestHeader(name, options.headers[name]);
      });

      if (options.withCredentials) {
        xhr.withCredentials = true;
      }

      xhr.send(options.data);
    } catch (e) {
      reject(e);
    }
  });

  promise.cancel = function () {
    xhr.abort();
  };

  return promise;
}
